import { type FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getRouteApi } from '@tanstack/react-router';
import { X } from 'lucide-react';

import { cn } from '@/lib/utils';
import { parseObjectToSearchParams } from '@/utils/parseSearchParams';

import type { EstatesToolbarUrl } from '../constants/routes';
import type { EstatesFiltersSchemaType } from '../schemas';
import { EstateFilter } from './estate-filter';
import type { EstateFilterProps } from './estate-filter';

type EstateFilterDrawerProps = {
  url: EstatesToolbarUrl;
  creatorList?: EstateFilterProps['creatorList'];
  viewOptions?: EstateFilterProps['viewOptions'];
  cardClassName?: string;
  gridClassName?: string;
};

const FOCUSABLE_SELECTORS = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([type="hidden"]):not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const hasFilterValue = (value: unknown): boolean => {
  if (value == null) return false;

  if (Array.isArray(value)) {
    return value.some(hasFilterValue);
  }

  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return !Number.isNaN(value);
  }

  if (typeof value === 'string') {
    return value.trim().length > 0;
  }

  if (typeof value === 'object') {
    return Object.values(value).some(hasFilterValue);
  }

  return false;
};

export const EstateFilterDrawer: FC<EstateFilterDrawerProps> = ({
  url,
  creatorList,
  viewOptions,
  cardClassName,
  gridClassName,
}) => {
  const route = getRouteApi(url);
  const { t } = useTranslation();
  const search = route.useSearch();
  const navigate = route.useNavigate();

  const drawerOpen = Boolean(search.enableFilters);

  const panelRef = useRef<HTMLDivElement | null>(null);
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null);
  const [filtersVersion, setFiltersVersion] = useState(0);

  const closeDrawer = useCallback(() => {
    navigate({
      search: (prev) => ({
        ...prev,
        enableFilters: false,
      }),
    });
  }, [navigate]);

  useEffect(() => {
    if (!drawerOpen) {
      return;
    }

    previouslyFocusedElementRef.current = (document.activeElement as HTMLElement | null) ?? null;

    const panel = panelRef.current;
    if (panel) {
      const focusable = panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS);
      if (focusable.length > 0) {
        focusable[0].focus();
      } else {
        panel.focus();
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeDrawer();
        return;
      }

      if (event.key === 'Tab') {
        const panelEl = panelRef.current;
        if (!panelEl) return;

        const focusableElements = panelEl.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS);
        if (focusableElements.length === 0) {
          event.preventDefault();
          return;
        }

        const first = focusableElements[0];
        const last = focusableElements[focusableElements.length - 1];
        const current = document.activeElement as HTMLElement | null;

        if (event.shiftKey) {
          if (!current || current === first) {
            event.preventDefault();
            last.focus();
          }
        } else if (!current || current === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      const previous = previouslyFocusedElementRef.current;
      if (previous && typeof previous.focus === 'function') {
        previous.focus();
      }
    };
  }, [closeDrawer, drawerOpen]);

  const { view, filters, filterOwn } = search;
  const parsedFilters = useMemo(
    () =>
      filters
        ? parseObjectToSearchParams<EstatesFiltersSchemaType>(filters as EstatesFiltersSchemaType)
        : undefined,
    [filters]
  );
  const hasFilters = useMemo(
    () => (parsedFilters ? hasFilterValue(parsedFilters) : false),
    [parsedFilters]
  );

  const searchParams: EstateFilterProps['searchParams'] = useMemo(
    () => ({
      view,
      filters,
      filterOwn,
      enableFilters: true,
    }),
    [view, filters, filterOwn]
  );

  const handleUpdateSearchParams = (filters: EstatesFiltersSchemaType) => {
    navigate({
      search: (prev) => ({
        ...prev,
        filters,
      }),
    });
  };

  const handleClearFilters = () =>
    navigate({
      search: (prev) => ({
        ...prev,
        filters: undefined,
      }),
    }).then(() => {
      setFiltersVersion((v) => v + 1);
    });

  const clearAllFiltersButton = hasFilters ? (
    <button
      type="button"
      onClick={handleClearFilters}
      className="mb-4 flex w-full items-center justify-center gap-2 rounded-[6px] border border-primary-light bg-white px-4 py-2 text-sm font-semibold text-text-primary hover:bg-primary-light hover:text-white"
    >
      <X className="h-4 w-4 stroke-[2px]" />
      {t('clearAllFilters', 'Clear All Filters')}
    </button>
  ) : null;

  if (!drawerOpen) {
    return null;
  }

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: overlay is a non-semantic clickable backdrop
    // biome-ignore lint/a11y/useKeyWithClickEvents: overlay close is handled via mouse click and global Escape key
    <div
      className="fixed inset-0 z-40 flex items-stretch justify-end bg-black/30"
      onClick={closeDrawer}
    >
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: this handler only stops propagation of clicks */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('filter')}
        tabIndex={-1}
        className={cn(
          'ml-auto flex h-full w-full max-w-full flex-col bg-white shadow-2xl',
          'sm:w-[360px] md:w-[380px] lg:w-[400px]'
        )}
        onClick={(event) => {
          event.stopPropagation();
        }}
      >
        <div className="flex justify-between border-b px-4 py-3">
          <div className="flex flex-col gap-1 pt-4">
            <h2 className="text-lg font-semibold leading-snug text-text-primary">
              {t('filterEstates', 'Filter Estates')}
            </h2>
            <p className="text-sm font-normal text-text-secondary">
              {t('filterEstatesDescription', 'Apply filters to narrow down your estate listings')}
            </p>
          </div>

          <button type="button" onClick={closeDrawer} className="ml-2 self-start">
            <X className="ml-auto size-4 cursor-pointer stroke-[2px] text-text-primary" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {clearAllFiltersButton}
          <EstateFilter
            key={filtersVersion}
            creatorList={creatorList}
            searchParams={searchParams}
            viewOptions={viewOptions}
            cardClassName={cardClassName}
            gridClassName={gridClassName}
            onUpdateSearchParams={handleUpdateSearchParams}
          />
        </div>
      </div>
    </div>
  );
};
